// components/ui/BulkUploadProgress.tsx
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    CheckCircle, AlertCircle, Loader2, X, Download
} from 'lucide-react';

// ============================================================
// TYPES
// ============================================================

interface BulkUploadStep {
    id: string;
    label: string;
    icon: React.ElementType;
    status: 'pending' | 'active' | 'completed' | 'error';
}

interface BulkUploadProgressProps {
    isOpen: boolean;
    onClose: () => void;
    steps: BulkUploadStep[];
    progress: number;
    status: 'processing' | 'completed' | 'failed';
    stats: {
        totalRows: number;
        successCount: number;
        errorCount: number;
        categoriesCreated: number;
    };
    onDownloadErrors?: () => void;
    errorMessage?: string;
}

// ============================================================
// COMPONENT
// ============================================================

export function BulkUploadProgress({
    isOpen,
    onClose,
    steps,
    progress,
    status,
    stats,
    onDownloadErrors,
}: BulkUploadProgressProps) {
    const size = 160;
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                >
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={status === 'completed' || status === 'failed' ? onClose : undefined}
                    />
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 30 }}
                        className="relative bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                    >
                        {/* Header */}
                        <div className={clsx(
                            'px-6 py-4',
                            status === 'completed' && 'bg-gradient-to-r from-emerald-600 to-teal-700',
                            status === 'failed' && 'bg-gradient-to-r from-rose-600 to-red-700',
                            status === 'processing' && 'bg-gradient-to-r from-teal-600 to-teal-700'
                        )}>
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-bold text-white">
                                    {status === 'processing' && 'Processing Upload...'}
                                    {status === 'completed' && 'Upload Complete!'}
                                    {status === 'failed' && 'Upload Failed'}
                                </h2>
                                {(status === 'completed' || status === 'failed') && (
                                    <button onClick={onClose} className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors" type="button">
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-6">
                            {/* Progress Ring */}
                            <div className="flex justify-center">
                                <div className="relative" style={{ width: size, height: size }}>
                                    <svg width={size} height={size} className="transform -rotate-90">
                                        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor"
                                            strokeWidth={strokeWidth} className="text-slate-200 dark:text-slate-700" />
                                        <motion.circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor"
                                            strokeWidth={strokeWidth} strokeLinecap="round"
                                            strokeDasharray={circumference}
                                            initial={{ strokeDashoffset: circumference }}
                                            animate={{ strokeDashoffset: offset }}
                                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                                            className={clsx(
                                                status === 'completed' && 'text-emerald-500',
                                                status === 'failed' && 'text-rose-500',
                                                status === 'processing' && 'text-teal-500'
                                            )} />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        {status === 'processing' && (
                                            <>
                                                <Loader2 size={32} className="text-teal-500 animate-spin mb-1" />
                                                <span className="text-2xl font-bold text-slate-800">{progress}%</span>
                                            </>
                                        )}
                                        {status === 'completed' && <CheckCircle size={40} className="text-emerald-500" />}
                                        {status === 'failed' && <AlertCircle size={40} className="text-rose-500" />}
                                    </div>
                                </div>
                            </div>

                            {/* Steps */}
                            <div className="space-y-2">
                                {steps.map((step) => (
                                    <div key={step.id} className={clsx(
                                        'flex items-center gap-3 px-3 py-2 rounded-lg transition-all',
                                        step.status === 'active' && 'bg-teal-50 border border-teal-200',
                                        step.status === 'completed' && 'bg-emerald-50',
                                        step.status === 'error' && 'bg-rose-50'
                                    )}>
                                        <div className={clsx(
                                            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                                            step.status === 'completed' && 'bg-emerald-100',
                                            step.status === 'active' && 'bg-teal-100',
                                            step.status === 'error' && 'bg-rose-100',
                                            step.status === 'pending' && 'bg-slate-100'
                                        )}>
                                            {step.status === 'completed' && <CheckCircle size={16} className="text-emerald-600" />}
                                            {step.status === 'active' && <Loader2 size={16} className="text-teal-600 animate-spin" />}
                                            {step.status === 'error' && <AlertCircle size={16} className="text-rose-600" />}
                                            {step.status === 'pending' && <step.icon size={16} className="text-slate-400" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={clsx(
                                                'text-sm font-medium',
                                                step.status === 'active' && 'text-teal-700',
                                                step.status === 'completed' && 'text-emerald-700',
                                                step.status === 'error' && 'text-rose-700',
                                                step.status === 'pending' && 'text-slate-400'
                                            )}>{step.label}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Stats */}
                            {(status === 'completed' || status === 'failed') && (
                                <div className="grid grid-cols-4 gap-2">
                                    <StatBadge label="Total" value={stats.totalRows} color="slate" />
                                    <StatBadge label="Success" value={stats.successCount} color="emerald" />
                                    <StatBadge label="Errors" value={stats.errorCount} color="rose" />
                                    <StatBadge label="Categories" value={stats.categoriesCreated} color="blue" />
                                </div>
                            )}

                            {/* Error download */}
                            {stats.errorCount > 0 && onDownloadErrors && (
                                <button onClick={onDownloadErrors}
                                    className="w-full py-2.5 bg-rose-50 border border-rose-200 rounded-xl text-sm font-medium text-rose-700 hover:bg-rose-100 transition-colors flex items-center justify-center gap-2"
                                    type="button">
                                    <Download size={14} /> Download Error Report
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function StatBadge({ label, value, color }: { label: string; value: number; color: 'slate' | 'emerald' | 'rose' | 'blue' }) {
    const colors: Record<string, string> = {
        slate: 'bg-slate-100 text-slate-700',
        emerald: 'bg-emerald-100 text-emerald-700',
        rose: 'bg-rose-100 text-rose-700',
        blue: 'bg-blue-100 text-blue-700',
    };
    return (
        <div className={clsx('text-center py-2 rounded-lg', colors[color])}>
            <p className="text-[10px] font-medium opacity-75">{label}</p>
            <p className="text-lg font-bold">{value}</p>
        </div>
    );
}